import { useOutletContext } from "react-router-dom";
import { EventOutletContext } from "../_layout";
import { type Path } from "@/router";
import EmptyState from "@/components/EmptyState";
import SectionHeading from "@/components/SectionHeading";
import ListItem from "@/components/ListItem";
import DetailsCard from "@/components/DetailsCard";
import Table, { TableDetails } from "@/components/Table";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import AvatarDetails from "@/components/AvatarDetails";
import { type CellContext } from "@tanstack/react-table";
import { useMutation } from "@apollo/client";
import { UPDATE_EVENT_MUTATION, FINALIZE_RECRUITMENT_EVENT_MUTATION } from "../../../queries";
import { useState, useEffect } from "react";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { useForm } from "react-hook-form";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";
import EditEventDetailsCard from "./_components/EditEventDetailsCard";
import EditEventSpendCard from "./_components/EditEventSpendCard";
import EditEventAttendanceCard from "./_components/EditEventAttendanceCard";
import EditEventRecapCard from "./_components/EditEventRecapCard";
import EditOverallFeedbackCard from "./_components/EditOverallFeedbackCard";
import EditCommentsQuotesCard from "./_components/EditCommentsQuotesCard";
import AddEventStaffDialog from "./_components/AddEventStaffDialog";
import { RemoveStaffConfirmationDialog } from "./_components/RemoveStaffConfirmationDialog";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import IconButton from "@/components/IconButton";
import { useRecruitmentEventPhotoUpload } from "@/hooks/useRecruitmentEventPhotoUpload";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { PhotoGallery, Photo } from "@/components/PhotoGallery";
import { useRecruitmentPhotoDeletion } from "@/hooks/useRecruitmentPhotoDeletion";
import { useMobilePhotoDownload } from "@/hooks/useMobilePhotoDownload";
import useCapabilities, { useCanViewRecruitingEventRecapsBool, useDenyAuthorization } from "@/hooks/useCapabilities";
import { useParams } from "react-router-dom";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import { getEventTypeName } from "../../utils";
import { showSnackbar } from "@/lib/snackbarUtils";

interface StaffAttendee {
  id: string;
  displayName: string;
  primarySection?: {
    id: string;
    name: string;
  };
  email?: string;
  pgaId?: string;
  profilePhoto?: string;
  phoneNumbers?: {
    phoneNumber: string;
    phoneType: string;
    phoneFormatted: string;
  }[];
}

interface EnumOption {
  name: string;
  description: string;
}

// Form data interfaces
interface EventDetailsFormData {
  recruitmentEventType: string;
  purpose: string;
  pgaRepresentation: string[];
  supportRequested: string[];
  additionalComments: string;
}

interface EventSpendFormData {
  strategicFunding: string;
  estimatedExpenses: number;
  sponsorshipAsk: number;
}

interface EventAttendanceFormData {
  estimatedParents: number;
  estimatedCoaches: number;
  estimatedOtherAttendees: number;
}

interface EventRecapFormData {
  eventRecap: string;
}

interface OverallFeedbackFormData {
  overallFeedback: string;
  eventRating: number;
}

interface CommentsQuotesFormData {
  commentsAndQuotes: string;
}

export default function RecapPage() {
  const { eventData, enumOptionsData, eventRefetch } = useOutletContext<EventOutletContext>();
  const { setError } = useErrorBanner();
  const { eventSlug } = useParams();
  const basePath = useEventsBasePath();
  const { trackEvent } = useAmplitude();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canExportRecruitingEventDetails = useCanViewRecruitingEventRecapsBool();
  const denyAuthorization = useDenyAuthorization();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [currentEventData, setCurrentEventData] = useState(eventData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [showRemoveStaffDialog, setShowRemoveStaffDialog] = useState(false);
  const [staffToRemove, setStaffToRemove] = useState<StaffAttendee | null>(null);

  const { Menu, setMenu } = useTableRowMenu();

  const clearEditingSection = () => setEditingSection(null);

  // Mutation for updating event
  const [updateEventMutation] = useMutation(UPDATE_EVENT_MUTATION);

  // Mutation for finalizing recruitment event
  const [finalizeRecruitmentEventMutation] = useMutation(FINALIZE_RECRUITMENT_EVENT_MUTATION);

  // Update currentEventData when eventData becomes available
  useEffect(() => {
    if (eventData) {
      setCurrentEventData(eventData);
    }
  }, [eventData]);

  // Redirect if user doesn't have permission to view recap page
  useEffect(() => {
    if (!eventData || !capabilitiesLoaded) {
      return;
    }

    const isRecruitment = getEventTypeName(eventData) === "Recruitment";
    const isRecruitingFinalized = !!eventData?.recruitmentEventAttributes?.isFinalized;

    // Recap tab should be available if the recruiting event is not finalized
    // or the user has the ability to export recruiting event details
    const hasRecapAccess = !isRecruitingFinalized || canExportRecruitingEventDetails;

    // If it's not a recruitment event or user doesn't have access, redirect to event home
    if (!isRecruitment || !hasRecapAccess) {
      denyAuthorization(`${basePath}/${eventSlug}` as Path);
    }
  }, [eventData, capabilitiesLoaded, canExportRecruitingEventDetails, denyAuthorization, eventSlug, basePath]);

  const recruitmentData = currentEventData?.recruitmentEventAttributes;

  // Initialize photo deletion hook
  const { deletePhoto } = useRecruitmentPhotoDeletion({
    eventId: currentEventData?.id || "",
    currentPhotoUploads: currentEventData?.recruitmentEventAttributes?.photoUploads || [],
    recruitmentData: recruitmentData || {},
    onPhotoDeleted: (newPhotoUploads) => {
      setCurrentEventData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          recruitmentEventAttributes: {
            ...prevData.recruitmentEventAttributes,
            photoUploads: newPhotoUploads
          }
        };
      });
    }
  });
  const staffAttendees = recruitmentData?.staffAttendees || [];

  // Create forms with current data
  const eventDetailsForm = useForm<EventDetailsFormData>({
    defaultValues: {
      recruitmentEventType: recruitmentData?.recruitmentEventType || "",
      purpose: recruitmentData?.purpose || "",
      pgaRepresentation: recruitmentData?.pgaRepresentation || [],
      supportRequested: recruitmentData?.supportRequested || [],
      additionalComments: recruitmentData?.additionalComments || ""
    }
  });

  const eventSpendForm = useForm<EventSpendFormData>({
    defaultValues: {
      strategicFunding: recruitmentData?.strategicFunding || "",
      estimatedExpenses: recruitmentData?.estimatedExpenses || 0,
      sponsorshipAsk: recruitmentData?.sponsorshipAsk || 0
    }
  });

  const eventAttendanceForm = useForm<EventAttendanceFormData>({
    defaultValues: {
      estimatedParents: recruitmentData?.estimatedParents || 0,
      estimatedCoaches: recruitmentData?.estimatedCoaches || 0,
      estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees || 0
    }
  });

  const eventRecapForm = useForm<EventRecapFormData>({
    defaultValues: {
      eventRecap: recruitmentData?.eventRecap || ""
    }
  });

  const overallFeedbackForm = useForm<OverallFeedbackFormData>({
    defaultValues: {
      overallFeedback: recruitmentData?.overallFeedback || "",
      eventRating: recruitmentData?.eventRating || 0
    }
  });

  const commentsQuotesForm = useForm<CommentsQuotesFormData>({
    defaultValues: {
      commentsAndQuotes: recruitmentData?.commentsAndQuotes || ""
    }
  });

  // Update form values when recruitmentData and enumOptionsData are available
  useEffect(() => {
    if (recruitmentData && enumOptionsData) {
      // Convert stored values to uppercase to match enum option names
      const convertToUpperCase = (value: string | undefined) => value?.toUpperCase() || "";
      const convertArrayToUpperCase = (values: string[] | undefined) => values?.map((v) => v.toUpperCase()) || [];

      // Use reset to update all form values at once, which triggers re-renders
      eventDetailsForm.reset({
        recruitmentEventType: convertToUpperCase(recruitmentData.recruitmentEventType),
        purpose: convertToUpperCase(recruitmentData.purpose),
        pgaRepresentation: convertArrayToUpperCase(recruitmentData.pgaRepresentation),
        supportRequested: convertArrayToUpperCase(recruitmentData.supportRequested),
        additionalComments: recruitmentData.additionalComments || ""
      });

      eventSpendForm.reset({
        strategicFunding: convertToUpperCase(recruitmentData.strategicFunding),
        estimatedExpenses: recruitmentData.estimatedExpenses || 0,
        sponsorshipAsk: recruitmentData.sponsorshipAsk || 0
      });

      eventAttendanceForm.reset({
        estimatedParents: recruitmentData.estimatedParents || 0,
        estimatedCoaches: recruitmentData.estimatedCoaches || 0,
        estimatedOtherAttendees: recruitmentData.estimatedOtherAttendees || 0
      });

      eventRecapForm.reset({
        eventRecap: recruitmentData.eventRecap || ""
      });

      overallFeedbackForm.reset({
        overallFeedback: recruitmentData.overallFeedback || "",
        eventRating: recruitmentData.eventRating || 0
      });

      commentsQuotesForm.reset({
        commentsAndQuotes: recruitmentData.commentsAndQuotes || ""
      });
    }
  }, [
    recruitmentData,
    enumOptionsData,
    eventDetailsForm,
    eventSpendForm,
    eventAttendanceForm,
    eventRecapForm,
    overallFeedbackForm,
    commentsQuotesForm
  ]);

  // Helper function to get enum description
  const getEnumDescription = (_enumName: string, enumValue: string, options?: EnumOption[]): string => {
    if (!options || !enumValue) return enumValue || "";

    const option = options.find((opt) => opt.name.toLowerCase() === enumValue.toLowerCase());
    return option?.description || enumValue;
  };

  // Helper function to get multiple enum descriptions
  const getMultipleEnumDescriptions = (enumValues: string[], options?: EnumOption[]): string => {
    if (!enumValues || enumValues.length === 0) return "";

    return enumValues
      .map((value) => getEnumDescription("", value, options))
      .filter((desc) => desc)
      .join(", ");
  };

  // Helper function to get event rating description
  const getEventRatingDescription = (rating: number): string => {
    const ratingDescriptions: Record<number, string> = {
      1: "No value / poor turnout",
      2: "Low value / low turnout",
      3: "Mixed value / ok turnout",
      4: "Solid value / good turnout",
      5: "High value / great turnout"
    };
    return ratingDescriptions[rating] || "";
  };

  const staffColumns = [
    {
      accessorKey: "displayName",
      header: "Name",
      cell: ({ row }: CellContext<StaffAttendee, unknown>) => (
        <AvatarDetails
          title={row.original.displayName}
          text={row.original.pgaId || ""}
          picture={row.original.profilePhoto}
        />
      )
    },
    {
      accessorKey: "primarySection",
      header: "Section",
      cell: ({ row }: CellContext<StaffAttendee, unknown>) => row.original.primarySection?.name || "-"
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }: CellContext<StaffAttendee, unknown>) => row.original.email || "-"
    },
    {
      accessorKey: "phoneNumbers",
      header: "Phone",
      cell: ({ row }: CellContext<StaffAttendee, unknown>) => {
        const primaryPhone =
          row.original.phoneNumbers?.find((phone) => phone.phoneType === "mobile") ||
          row.original.phoneNumbers?.find((phone) => phone.phoneType === "home") ||
          row.original.phoneNumbers?.[0];
        return primaryPhone?.phoneFormatted || "-";
      }
    },
    {
      cell(cellProps: CellContext<StaffAttendee, unknown>) {
        const actions: Action[] = [
          {
            text: "View Person",
            onClick: () => {
              window.open(`/people/${cellProps.row.original.pgaId}/details/overview`, "_blank");
            }
          },
          {
            text: "Remove from Event",
            destructive: true,
            onClick: () => {
              setStaffToRemove(cellProps.row.original);
              setShowRemoveStaffDialog(true);
            }
          }
        ];

        return (
          <div
            className="text-right flex flex-row justify-end"
            data-testid="menu-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              id={actionId(cellProps.row.id)}
              onClick={() => setMenu({ rowId: cellProps.row.id, actions })}
              data-testid="actions"
              title="actions"
              name="more_vert"
              iconClassName="font-extrabold"
              skipMargin
            />
          </div>
        );
      },
      id: "actions",
      size: 100
    }
  ];

  // Check if event is pending and not finalized (for Finalize Recap button)
  const isPending = currentEventData?.status?.toLowerCase() === "pending";
  const isFinalized = recruitmentData?.isFinalized || false;
  const showFinalizeButton = isPending && !isFinalized;

  const onSuccess = (updatedData: any, field: string) => {
    clearEditingSection();
    setCurrentEventData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        recruitmentEventAttributes: {
          ...prevData.recruitmentEventAttributes,
          ...updatedData
        }
      };
    });
    showSnackbar("Recap details updated");
    trackEvent(
      EVENT_ANALYTICS_EVENTS.recruitmentRecapUpdated,
      getEventAnalyticsData({
        source: "recap",
        eventId: currentEventData?.id,
        eventSlug,
        action: "update",
        field
      })
    );
  };

  const onError = (error: string) => {
    clearEditingSection();
    setError(error);
  };

  // Handler for showing the finalize confirmation dialog
  const handleFinalizeRecruitmentEvent = () => {
    setShowFinalizeDialog(true);
  };

  // Handler for actually finalizing the recruitment event
  const handleConfirmFinalizeRecruitmentEvent = async () => {
    if (!currentEventData?.id) {
      setError("Event ID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: responseData } = await finalizeRecruitmentEventMutation({
        variables: {
          id: currentEventData.id
        }
      });

      const { success } = responseData.finalizeRecruitmentEvent;
      if (success) {
        // Update local state to reflect that the event is now finalized
        setCurrentEventData((prevData) => {
          if (!prevData) return prevData;
          return {
            ...prevData,
            recruitmentEventAttributes: {
              ...prevData.recruitmentEventAttributes,
              isFinalized: true
            }
          };
        });
        showSnackbar("Recruitment event finalized");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.recruitmentRecapFinalized,
          getEventAnalyticsData({
            source: "recap",
            eventId: currentEventData.id,
            eventSlug,
            action: "finalize"
          })
        );
        setShowFinalizeDialog(false);
      } else {
        setError(responseData.finalizeRecruitmentEvent.message || "Failed to finalize recruitment event");
      }
    } catch (error) {
      setError("Failed to finalize recruitment event");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form handlers for each section
  const handleEventDetailsSubmit = async (formData: EventDetailsFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: formData.recruitmentEventType,
              purpose: formData.purpose,
              pgaRepresentation: formData.pgaRepresentation,
              supportRequested: formData.supportRequested,
              additionalComments: formData.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "event_details");
        // Refetch event data to ensure CSV export has updated event details
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to update event details");
      }
    } catch (error) {
      onError("Failed to update event details");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventSpendSubmit = async (formData: EventSpendFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: formData.strategicFunding,
              estimatedExpenses: formData.estimatedExpenses,
              sponsorshipAsk: formData.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "event_spend");
        // Refetch event data to ensure CSV export has updated event spend
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to update event spend");
      }
    } catch (error) {
      onError("Failed to update event spend");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventAttendanceSubmit = async (formData: EventAttendanceFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: formData.estimatedParents,
              estimatedCoaches: formData.estimatedCoaches,
              estimatedOtherAttendees: formData.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "event_attendance");
        // Refetch event data to get updated totalAttendees calculated by backend
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to update event attendance");
      }
    } catch (error) {
      onError("Failed to update event attendance");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventRecapSubmit = async (formData: EventRecapFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: formData.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "event_recap");
      } else {
        onError(responseData.updateEvent.message || "Failed to update event recap");
      }
    } catch (error) {
      onError("Failed to update event recap");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverallFeedbackSubmit = async (formData: OverallFeedbackFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: formData.overallFeedback,
              eventRating: formData.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "overall_feedback");
        // Refetch event data to ensure CSV export has updated event rating
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to update overall feedback");
      }
    } catch (error) {
      onError("Failed to update overall feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentsQuotesSubmit = async (formData: CommentsQuotesFormData) => {
    setIsSubmitting(true);
    try {
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData?.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: formData.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess(formData, "comments_quotes");
      } else {
        onError(responseData.updateEvent.message || "Failed to update comments and quotes");
      }
    } catch (error) {
      onError("Failed to update comments and quotes");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddStaff = async (newStaffAttendees: StaffAttendee[]) => {
    if (!currentEventData?.id) {
      setError("Event ID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      // Combine existing staff with new staff
      const allStaffAttendees = [...(recruitmentData?.staffAttendees || []), ...newStaffAttendees];

      // Extract just the IDs for the mutation
      const staffAttendeesIds = allStaffAttendees.map((staff) => staff.id);

      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized,
              staffAttendeesIds: staffAttendeesIds
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess({ staffAttendees: allStaffAttendees }, "staff_add");
        setShowAddStaffDialog(false);
        showSnackbar("Staff members added");
        // Refetch event data to ensure CSV export has updated staff attendees
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to add staff members");
      }
    } catch (error) {
      onError("Failed to add staff members");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveStaffFromEvent = async (staffId: string) => {
    if (!currentEventData?.id) {
      setError("Event ID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      // Remove the staff member from the list
      const updatedStaffAttendees = (recruitmentData?.staffAttendees || []).filter((staff) => staff.id !== staffId);

      // Extract just the IDs for the mutation
      const staffAttendeesIds = updatedStaffAttendees.map((staff) => staff.id);

      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: currentEventData.id,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized,
              staffAttendeesIds: staffAttendeesIds
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onSuccess({ staffAttendees: updatedStaffAttendees }, "staff_remove");
        setShowRemoveStaffDialog(false);
        setStaffToRemove(null);
        // Refetch event data to ensure CSV export has updated staff attendees
        await eventRefetch();
      } else {
        onError(responseData.updateEvent.message || "Failed to remove staff member");
      }
    } catch (error) {
      onError("Failed to remove staff member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemoveStaff = () => {
    if (staffToRemove) {
      handleRemoveStaffFromEvent(staffToRemove.id);
    }
  };

  const photoUploads = currentEventData?.recruitmentEventAttributes?.photoUploads ?? [];

  // Convert photo uploads to Photo interface format
  const photos: Photo[] = photoUploads.map((url, index) => {
    // Extract filename from URL
    const urlParts = url.split("/");
    const filename = urlParts[urlParts.length - 1];

    return {
      id: `photo-${index}`,
      url,
      filename
    };
  });

  const { uploadPhoto } = useRecruitmentEventPhotoUpload({
    eventId: currentEventData?.id || "",
    currentPhotoUploads: photoUploads,
    recruitmentData,
    onPhotoUploadSuccess: (newPhotoUploads) => {
      setCurrentEventData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          recruitmentEventAttributes: {
            ...prevData.recruitmentEventAttributes,
            photoUploads: newPhotoUploads
          }
        };
      });
    }
  });

  const handlePhotoUpload = uploadPhoto;

  // Initialize mobile photo download hook
  const { downloadPhoto, isMobile } = useMobilePhotoDownload();

  // Handle photo deletion
  const handlePhotoDelete = async (photoId: string) => {
    await deletePhoto(photoId);
  };

  // Handle photo download with mobile-specific functionality
  const handlePhotoDownload = async (photo: Photo) => {
    await downloadPhoto({
      photo,
      onSuccess: () => {
        if (isMobile) {
          showSnackbar("Photo opened for saving", {
            subtitle: "Long-press to save to Photos.",
            variant: "info"
          });
        } else {
          showSnackbar("Photo downloaded");
        }
      },
      onError: (error) => {
        console.error("Error downloading photo:", error);
        setError(`Failed to download photo: ${error}`);
      }
    });
  };

  return (
    <div className="flex flex-col">
      <Menu />
      <SectionHeading
        heading="Event Details"
        actions={
          showFinalizeButton
            ? [
                {
                  label: "Finalize Recap",
                  onClick: handleFinalizeRecruitmentEvent
                }
              ]
            : []
        }
      />
      <ListItem
        iconName="domain"
        iconBackground={true}
        overline="Section"
        headline={currentEventData?.section?.name || ""}
      />
      <ListItem
        iconName="person_add"
        iconBackground={true}
        overline="Total Leads"
        headline={currentEventData?.registrationCount?.toString() || "0"}
      />
      <SectionHeading
        heading="Recruitment Details"
        body="Confirm event details, spend, and provide overall feedback."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        <DetailsCard
          heading="Event Details"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Event Details")}
          options={[
            {
              iconName: "today",
              overline: "Recruitment Event Type",
              headline: getEnumDescription(
                "recruitmentEventType",
                recruitmentData?.recruitmentEventType || "",
                enumOptionsData?.recruitmentEventTypeOptions?.enumValues
              )
            },
            {
              iconName: "today",
              overline: "Purpose",
              headline: getEnumDescription(
                "purpose",
                recruitmentData?.purpose || "",
                enumOptionsData?.purposeOptions?.enumValues
              )
            },
            {
              iconName: "group",
              overline: "PGA Representation",
              headline: getMultipleEnumDescriptions(
                recruitmentData?.pgaRepresentation || [],
                enumOptionsData?.pgaRepresentationOptions?.enumValues
              )
            },
            {
              iconName: "group",
              overline: "Support Requested",
              headline: getMultipleEnumDescriptions(
                recruitmentData?.supportRequested || [],
                enumOptionsData?.supportRequestedOptions?.enumValues
              )
            },
            {
              iconName: "chat",
              overline: "Additional Comments",
              headline: recruitmentData?.additionalComments || ""
            }
          ]}
          renderEdit={() => (
            <EditEventDetailsCard
              form={eventDetailsForm}
              onSubmit={handleEventDetailsSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
              enumOptionsData={enumOptionsData}
            />
          )}
        />
        <DetailsCard
          heading="Event Spend"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Event Spend")}
          options={[
            {
              iconName: "attach_money",
              overline: "Strategic Funding",
              headline: getEnumDescription(
                "strategicFunding",
                recruitmentData?.strategicFunding || "",
                enumOptionsData?.strategicFundingOptions?.enumValues
              ),
              supportingText: "Event is being used for strategic funding"
            },
            {
              iconName: "attach_money",
              overline: "Expenses",
              headline: recruitmentData?.estimatedExpenses
                ? `$${currencyFormatter(recruitmentData.estimatedExpenses.toString())}`
                : "",
              supportingText: "Total of all staff's expenses for travel, lodging, meals, etc."
            },
            {
              iconName: "attach_money",
              overline: "Sponsorship Spend",
              headline: recruitmentData?.sponsorshipAsk
                ? `$${currencyFormatter(recruitmentData.sponsorshipAsk.toString())}`
                : ""
            }
          ]}
          renderEdit={() => (
            <EditEventSpendCard
              form={eventSpendForm}
              onSubmit={handleEventSpendSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
              enumOptionsData={enumOptionsData}
            />
          )}
        />
        <DetailsCard
          heading="Event Attendance"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Event Attendance")}
          options={[
            {
              iconName: "group",
              overline: "Parents",
              headline: recruitmentData?.estimatedParents?.toString() || ""
            },
            {
              iconName: "group",
              overline: "Coaches",
              headline: recruitmentData?.estimatedCoaches?.toString() || ""
            },
            {
              iconName: "group",
              overline: "Students/Athletes/Attendees",
              headline: recruitmentData?.estimatedOtherAttendees?.toString() || ""
            },
            {
              iconName: "how_to_reg",
              overline: "Total Attendees",
              headline: recruitmentData?.totalAttendees?.toString() || ""
            }
          ]}
          renderEdit={() => (
            <EditEventAttendanceCard
              form={eventAttendanceForm}
              onSubmit={handleEventAttendanceSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
            />
          )}
        />
      </div>
      <SectionHeading
        heading="Event Recap and Feedback"
        body="Please provide a recap of the event and any feedback you have for future improvements."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        <DetailsCard
          heading="Event Recap"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Event Recap")}
          options={[
            {
              iconName: "chat",
              overline: "Recap",
              headline: recruitmentData?.eventRecap || "",
              supportingText:
                "Setting, National and Section staff in attendance, speaking opportunities, highlights, marketing materials used, etc."
            }
          ]}
          renderEdit={() => (
            <EditEventRecapCard
              form={eventRecapForm}
              onSubmit={handleEventRecapSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
            />
          )}
        />
        <DetailsCard
          heading="Overall Feedback"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Overall Feedback")}
          options={[
            {
              iconName: "chat",
              overline: "Overall Feedback",
              headline: recruitmentData?.overallFeedback || "",
              supportingText: "Please include suggestions for improvements/reminders if attending in the future"
            },
            {
              iconName: "thumb_up",
              overline: "Event Rating",
              headline: recruitmentData?.eventRating
                ? `${recruitmentData.eventRating} - ${getEventRatingDescription(recruitmentData.eventRating)}`
                : "",
              supportingText: "Rate the effectiveness of this event for PGA Recruiting"
            }
          ]}
          renderEdit={() => (
            <EditOverallFeedbackCard
              form={overallFeedbackForm}
              onSubmit={handleOverallFeedbackSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
            />
          )}
        />
        <DetailsCard
          heading="Comments and Quotes"
          editingSection={editingSection}
          onEdit={() => setEditingSection("Comments and Quotes")}
          options={[
            {
              iconName: "3p",
              overline: "Quotes",
              headline: recruitmentData?.commentsAndQuotes || "",
              supportingText: "Please add any comments/quotes that could be shared with PGA Leadership"
            }
          ]}
          renderEdit={() => (
            <EditCommentsQuotesCard
              form={commentsQuotesForm}
              onSubmit={handleCommentsQuotesSubmit}
              onCancel={clearEditingSection}
              isSubmitting={isSubmitting}
            />
          )}
        />
      </div>
      <Table
        loading={false}
        data={staffAttendees}
        columns={staffColumns}
        renderFilteredEmptyState={() => (
          <EmptyState
            iconName="person"
            title="No PGA Staff at Event"
            caption="Add PGA staff members who assisted at the event."
          />
        )}
        renderDetails={() => (
          <TableDetails
            heading="PGA Staff at Event"
            body="Please add all PGA staff who attended and/or assisted at the event."
            ButtonComps={[
              () => (
                <Button icon="add" variant="outlined" onClick={() => setShowAddStaffDialog(true)}>
                  Add Event Staff
                </Button>
              )
            ]}
          />
        )}
      />

      <SectionHeading
        heading="Event Photos"
        body="Please share any photos from the event."
        actions={[
          {
            label: "Upload Photo(s)",
            onClick: handlePhotoUpload,
            icon: "upload",
            variant: "outlined"
          }
        ]}
      />

      {(() => {
        const photoUploads = currentEventData?.recruitmentEventAttributes?.photoUploads;
        return !photoUploads || photoUploads.length === 0 ? (
          <EmptyState
            iconName="photo"
            title="No Photos"
            caption="Please share any photos from the event."
            action={{
              children: "Upload Photo(s)",
              onClick: handlePhotoUpload,
              icon: "upload"
            }}
          />
        ) : (
          <div className="w-full mx-auto mt-8" style={{ maxWidth: "926px" }}>
            <PhotoGallery
              photos={photos}
              onDelete={handlePhotoDelete}
              onDownload={handlePhotoDownload}
              showActions={true}
            />
          </div>
        );
      })()}

      {/* Finalize Recap Confirmation Dialog */}
      <Dialog
        open={showFinalizeDialog}
        onClosed={() => setShowFinalizeDialog(false)}
        headline="Finalize Recap"
        className="sm:max-w-[423px] p-6 pr-2"
        actions={
          <>
            <Button variant="text" onClick={() => setShowFinalizeDialog(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleConfirmFinalizeRecruitmentEvent}
              disabled={isSubmitting}
              icon={isSubmitting ? "progress_activity" : undefined}
              iconClassName={isSubmitting ? "animate-spin w-6 h-6 !block" : undefined}
            >
              Finalize Recap
            </Button>
          </>
        }
      >
        <p>Are you sure you want to finalize this recap? PGA Recruiting will be notified.</p>
      </Dialog>

      {/* Add Event Staff Dialog */}
      <AddEventStaffDialog
        open={showAddStaffDialog}
        onClosed={() => setShowAddStaffDialog(false)}
        onSave={handleAddStaff}
        existingStaffAttendees={recruitmentData?.staffAttendees || []}
        isSubmitting={isSubmitting}
      />

      {/* Remove Staff Confirmation Dialog */}
      <RemoveStaffConfirmationDialog
        open={showRemoveStaffDialog}
        onClosed={() => {
          setShowRemoveStaffDialog(false);
          setStaffToRemove(null);
        }}
        onConfirm={handleConfirmRemoveStaff}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
